import { useSWRConfig } from "swr";
import { RelativeUrl } from "@adora/shared/utils/environment";
import post from "@adora/shared/utils/post";
import patch, { JsonValue } from "@adora/shared/utils/patch";
import deleteRequest from "@adora/shared/utils/delete";
import { User, UserAccountType, UserServerResponse } from "../types/user";
import { transformUser, UsersUrl } from "./use-users";
import { Role } from "@adora/shared/types/auth";

export interface CreateUserOptions {
  name: string;
  password: string;
  role: Role;
  accountType: UserAccountType;
  brandId?: string;
  brands?: string[];
  email?: string;
  rmnPublisherId?: number;
}

export interface UpdateUserOptions {
  id: number;
  name?: string;
  password?: string;
  brands?: string[];
  externalInfo?: JsonValue;
  email?: string;
}

const cacheKeyPattern = new RegExp(`^${RelativeUrl}/${UsersUrl}`);

const useMutateUser = () => {
  const { mutate } = useSWRConfig();

  const create = async ({
    name,
    password,
    role,
    accountType,
    brandId,
    brands,
    email,
    rmnPublisherId
  }: CreateUserOptions) => {
    const newUserResult = await post<UserServerResponse>(
      `${RelativeUrl}/${UsersUrl}`,
      {},
      {
        name,
        password,
        role,
        account_type: accountType,
        ...(brandId && { brand_id: brandId }),
        ...(brands && { brands }),
        ...(email && { email }),
        ...(rmnPublisherId && { rmn_publisher_id: rmnPublisherId })
      }
    ).then((result) => result.map((response) => transformUser([response])[0]));

    newUserResult.forEach(() =>
      mutate<User[]>((key) => typeof key === "string" && cacheKeyPattern.test(key), undefined, { revalidate: true })
    );

    return newUserResult.getOrElse((e) => console.error(e.message));
  };

  const update = async ({ id, name, password, brands, externalInfo, email }: UpdateUserOptions) => {
    const serverUserResult = await patch<UserServerResponse>(
      `${RelativeUrl}/${UsersUrl}/${id}`,
      {},
      {
        ...(name && { name }),
        ...(password && { password }),
        ...(brands && { brands }),
        ...(externalInfo !== undefined && { external_info: externalInfo }),
        ...(email !== undefined && { email })
      }
    ).then((result) => result.map((response) => transformUser([response])[0]));

    serverUserResult.forEach(() =>
      mutate<User[]>((key) => typeof key === "string" && cacheKeyPattern.test(key), undefined, { revalidate: true })
    );

    return serverUserResult.getOrElse((e) => console.error(e.message));
  };

  const remove = async (id: number) => {
    await deleteRequest(`${RelativeUrl}/${UsersUrl}/${id}`);
    mutate<User[]>((key) => typeof key === "string" && cacheKeyPattern.test(key), undefined, { revalidate: true });
  };

  const restore = async (id: number) => {
    const restoredUserResult = await patch<UserServerResponse>(`${RelativeUrl}/${UsersUrl}/${id}/restore`);

    restoredUserResult.forEach(() =>
      mutate<User[]>((key) => typeof key === "string" && cacheKeyPattern.test(key), undefined, { revalidate: true })
    );

    return restoredUserResult.getOrElse((e) => console.error(e.message));
  };

  return {
    create,
    update,
    remove,
    restore
  };
};

export default useMutateUser;
